import {
  defineComponent,
  computed,
  PropType,
} from 'vue'
import { sortBy } from 'lodash'
import Option from '@xhs/yam-beer/cmp/overlayers/Option/Option.vue'
import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
import Popover from '../../overlayers/Popover/Popover.vue'
import useOptions, {
  ExtendItem, ItemType,
  UseOptionsParams,
  GetListFn,
} from '../../../compose/options/useOptions'
import BeerInput from './Input.vue'

function getSortedOptions(
  sortOnInput: boolean,
  options: any[],
  value: string,
) {
  if (!sortOnInput || !value) {
    return options
  }
  return sortBy(
    options.filter(i => i.text.indexOf(value) > -1),
    i => i.text.indexOf(value),
  )
}

/**
 * Input 输入框带下拉框便捷选择。
 *
 * 类似 Ant Design 的 AutoComplete
 *
 * @see 继承InputGroup #InputGroup
 */
export default defineComponent({
  name: 'InputWithOption',
  props: {
    modelValue: {
      type: String,
      default: undefined,
    },
    /**
     * 固定选项列表
     */
    options: {
      type: Array as PropType<ItemType[]>,
      default: (): ItemType[] => ([]),
    },
    /**
     * 选项需要从方法中调用
     * - 配合 filterable 属性可以根据筛选值来搜索
     */
    getList: {
      type: Function as PropType<GetListFn>,
      required: false,
    },
    /**
     * useOption 的自定义选项
     */
    optionParams: {
      type: Object as PropType<UseOptionsParams>,
      default: () => ({}),
    },
    /**
     * @ignore
     * 是否只读显示
     */
    readonly: {
      type: Boolean,
      default: false,
    },
    /**
     * 选项是否排序
     */
    sortOnInput: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props: any, { slots, emit, attrs }) {

    const optionsHook = useOptions({
      options: computed(() => props.options),
      getList: props.getList || undefined,
      ...props.optionParams,
      filterFn: undefined,
    })

    const onChange = (o: ExtendItem) => {
      const value = o.value
      if (value !== null) {
        /**
         * 输入值触发的事件
         * 可以用 v-model 双向绑定
         *
         * @property {ItemType} value - 选项值
         */
        emit('update:modelValue', value)
      }
    }

    const sortedOptions = computed(() => {
      const ops = getSortedOptions(
        props.sortOnInput,
        optionsHook.items.value,
        props.modelValue,
      )
      return ops
    })
    return () => {
      const passSlots: any = {
        ...slots,
      }
      if (sortedOptions.value.length > 0) {
        passSlots.popover = () => {
          const children = sortedOptions.value.map(o => (
            <Option
              key={o.id}
              {...{
                onMousedown: () => {
                  onChange(o)
                },
              }}
            >
              {o.text}
            </Option>
          ))
          return (
            <NewBsBox bs={{ maxHeight: 310, overflow: 'auto' }}>
              {children}
            </NewBsBox>
          )
        }
      }
      return (
        <Popover
          // @ts-ignore
          trigger="focus"
          hidden={props.readonly || attrs.disabled || sortedOptions.value.length === 0 }
          v-slots={passSlots}
        >
          <BeerInput
            ref="input"
            {...({
              ...attrs,
              modelValue: props.modelValue,
              'onUpdate:modelValue': (v: string) => {
                emit('update:modelValue', v)
              },
            })}
            readonly={props.readonly}
          />
        </Popover>
      )
    }
  },
})
